// @ts-nocheck

import * as React from "react";
import {
  Outlet,
  createRootRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import Nav from "../components/Nav";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { useEffect, useState, useRef } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import DataRetrieve from "../components/DataRetrieve";
import DataSend from "../components/DataSend";
import CheckLocationPerms from "../components/LocationPermissionCheck";
import DebugLog from "../components/DebugLog";
// import { usePostHog } from "posthog-js/react";
import getAuth from "../msalAuth";
import { Info } from "lucide-react";
import { db } from "../db/db";

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
    const dialogRef = useRef<HTMLDialogElement | null>(null);
  
  // const posthog = usePostHog();
  const location = useLocation();
  const navigate = useNavigate();

  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const handleLogin = () => {
    instance
      .loginRedirect(loginRequest)
      .catch((e) => console.error("MSAL login error", e));
  };

  const clearDB = () => {
        db.delete();
        localStorage.clear();

  }
  // Ensure any redirect response is handled and try to refresh tokens on page load
  useEffect(() => {
    if (!instance) return;
    let mounted = true;
    let interactionRequired = false;
    // Handle redirect and then attempt a silent token acquisition to populate id token claims.
    instance
      .handleRedirectPromise()
      .then(async () => {
        const accts = instance.getAllAccounts();
        if (accts && accts.length > 0) {
          const acct = accts[0];
          try {
            instance.setActiveAccount(acct);
            try {
              if (process.env.NODE_ENV !== "production")
                console.log("MSAL account idTokenClaims:", acct.idTokenClaims, "account:", acct);
            } catch (e) {}
          } catch (e) {}
          try {
            const resp = await instance.acquireTokenSilent({ scopes: ["openid", "profile", "email"], account: acct });
            if (!mounted) return;
            // prefer the claims-based EmployeeID when available
            const auth = getAuth();
            if (auth && auth.id !== undefined && auth.id !== null) {
              setUserID(auth.id);
              try {
                localStorage.setItem("employeeId", String(auth.id));
                // mark that this client had a successful auth recently
                localStorage.setItem("pulse_last_auth", String(Date.now()));
              } catch (e) {}
            }
          } catch (err) {
            // If interaction required, don't force a redirect blindly — but remember
            // that interaction is required so we can decide after mount whether to
            // prompt the user to re-authenticate (only if they previously had a session).
            if (err instanceof InteractionRequiredAuthError || (err && err.errorCode && err.errorCode === "interaction_required")) {
              interactionRequired = true;
              if (process.env.NODE_ENV !== "production") {
                console.debug("MSAL: silent token failed with interaction_required; will consider interactive login if previously authenticated");
              }
            }
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) setClaimsLoaded(true);
        // If silent token acquisition indicated interaction is required, only
        // initiate an interactive login if this client previously had a session.
        try {
          const hadAuth = !!localStorage.getItem("pulse_last_auth") || !!localStorage.getItem("employeeId");
          const inMsalResponse = (window.location && (window.location.search.includes("code=") || window.location.hash.includes("#id_token") || window.location.hash.includes("msal")));
          if (interactionRequired && hadAuth && !inMsalResponse) {
            // Trigger an explicit login redirect so the user re-authenticates.
            if (process.env.NODE_ENV !== "production") console.debug("MSAL: triggering loginRedirect due to revoked/expired session");
            instance.loginRedirect(loginRequest).catch((e) => console.error("MSAL login redirect failed", e));
          }
        } catch (e) {}
      });
    return () => {
      mounted = false;
    };
  }, [instance]);
  const [claimsLoaded, setClaimsLoaded] = useState(false);
  const [userID, setUserID] = useState();
  const [time, setTime] = useState(Date.now());
  const [debugState, setDebugState] = useState(false);
  // const [interval, setInterval] = useState("TEST");
  useEffect(() => {
    const int = setInterval(() => setTime(Date.now()), 60000);
    return () => {
      clearInterval(int);
    };
  }, []);
  //   if ("geolocation" in navigator) {
  //     /* geolocation is available */
  //     navigator.geolocation.getCurrentPosition((position) => {
  // });
  //   } else {
  //     /* geolocation IS NOT available */
  //   }

  // Derive user ID from MSAL (prefer Employee ID from claims)
  useEffect(() => {
    if (isAuthenticated && accounts && accounts.length > 0) {
      const auth = getAuth();
      // If claims are already present and contain an employee id, use it
      if (auth && auth.id !== undefined && auth.id !== null) {
        setUserID(auth.id);
        try { localStorage.setItem("employeeId", String(auth.id)); } catch (e) {}
        return;
      }
      // Wait until we've attempted to load claims before falling back to account email
      if (!claimsLoaded) return;
      const acct = accounts[0];
      const fallbackId = acct.homeAccountId || acct.localAccountId || acct.username;
      setUserID(fallbackId);
    } else {
      setUserID(undefined);
    }
  }, [isAuthenticated, accounts]);
  
  
  useEffect(() => {
    if (!isAuthenticated) {
      if (location["pathname"] !== "/auth") {
        navigate({ to: "/auth" });
        localStorage.setItem("routeBeforeAuth", location["pathname"]);
      }
    }
  }, [isAuthenticated]);

  // When authentication becomes true, redirect back to the saved route or root
  useEffect(() => {
    if (isAuthenticated) {
      const routeBefore = localStorage.getItem("routeBeforeAuth");
      const target = routeBefore || "/";
      if (location.pathname !== target) {
        navigate({ to: target });
      }
      localStorage.removeItem("routeBeforeAuth");
    }
  }, [isAuthenticated, navigate]);

  return (
    <React.Fragment>
      <CheckLocationPerms />

      <div className="h-[calc(100dvh-64px)] relative overflow-auto px-3">
        <Outlet />
        {!isAuthenticated && location.pathname === "/auth" && (
          <div className="flex-col p-6">
            <div
              role="alert"
              className="alert alert-success shadow-lg mb-6 p-4 alert-vertical"
            >
              <Info />
              <h3 className="font-bold">
                Pulse has a new authentication system
              </h3>
              <div className="text-xs">
                Press the button below and use your First name and Last name as
                the username (johndoe for example)
              </div>
            </div>
            <div className="flex items-center justify-center self-center pb-6">
              <button onClick={handleLogin} className="btn btn-primary w-full">
                Log in
              </button>
            </div>
            <div className="card w-96 mt-6 p-2 bg-red-100 card-xs shadow-sm ">
              <div className="card-body text-">
                <h2 className="card-title">Having trouble?</h2>
                <p>If login isn't working, click the button below</p>
                <div className="justify-end card-actions">
                  <button
                    className="btn btn-error btn-xs"
                    onClick={() => dialogRef.current?.showModal()}
                  >
                    Clear Local Data
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end pt-6 self-center"></div>
          </div>
        )}
      </div>
      <dialog className="modal" ref={dialogRef}>
        <div className="modal-box">
          {/* <form method="dialog" onSubmit={() => setReadyToPost(1)}> */}
          <h3 className="text-l">
            Are you sure? This will clear any attendance data that hasn't been
            synced to the server.
          </h3>

          <div className="modal-action">
            <button
              className="btn w-full btn-error"
              // disabled={!timeAndReason}
              onClick={() => {
                dialogRef.current?.close();
                clearDB()
                // dbUpdate("end", endLocationForManual, 1);
              }}
            >
              Clear the data!
            </button>
          </div>

          {/* </form> */}
        </div>
      </dialog>
      {isAuthenticated && (
        <>
          <Nav />
          {userID && <DataRetrieve id={userID} refreshTick={time} />}
          {userID && <DataSend id={userID} refreshTick={time} />}
        </>
      )}
    </React.Fragment>
  );
}
